import { IMedicationDetails, IPrescriberDetails } from "../types/PrescriptionIngestionTypes";
import { determineIdentitySource } from "../../../consts/Patterns";

export type RxChangeResponseType = "APPROVED" | "APPROVED_WITH_CHANGES" | "DENIED";

export interface ICodeOption {
  code: string;
  label: string;
}

export const RESPONSE_TYPES: { value: RxChangeResponseType; label: string }[] = [
  { value: "APPROVED", label: "Approved" },
  { value: "APPROVED_WITH_CHANGES", label: "Approved with changes" },
  { value: "DENIED", label: "Denied" },
];

export const REQUEST_CODES: ICodeOption[] = [
  { code: "S", label: "Script clarification" },
  { code: "G", label: "Generic substitution" },
  { code: "T", label: "Therapeutic interchange" },
  { code: "D", label: "Drug use evaluation" },
  { code: "P", label: "Prior authorization required" },
  { code: "OS", label: "Out of stock" },
  { code: "U", label: "Prescriber authorization" },
];

export const DENIAL_REASON_CODES: ICodeOption[] = [
  { code: "AA", label: "Patient unknown to the provider" },
  { code: "AB", label: "Patient never under provider care" },
  { code: "AC", label: "Patient no longer under provider care" },
  { code: "AE", label: "Medication never prescribed for the patient" },
  { code: "AF", label: "Patient should contact provider first" },
  { code: "AL", label: "Change not appropriate" },
  { code: "AM", label: "Patient needs appointment" },
  { code: "AN", label: "Prescriber not associated with this practice or location" },
  { code: "AO", label: "No attempt will be made to obtain prior authorization" },
  { code: "AP", label: "Request already responded to by other means" },
  { code: "BE", label: "Medication denied at patient request" },
];

export const MAX_RELATES_TO_MESSAGE_ID_LENGTH = 35;
export const MAX_NOTE_LENGTH = 70;
const PLACEHOLDER_DRUG_DB_CODE = "000000";

export interface IRxChangeResponseForm {
  relatesToMessageId: string;
  messageRequestCode: string;
  responseType: RxChangeResponseType;
  note: string;
  denialReasonCode: string;
  denialReason: string;
  patientId: string;
  prescriber: IPrescriberDetails;
  medication: IMedicationDetails;
}

export interface IRxChangeResponsePrescriber {
  npi: string;
  deaNumber: string;
  firstName: string;
  lastName: string;
  practiceName?: string;
  addressLine1: string;
  city: string;
  state: string;
  postalCode: string;
  phone?: string;
  fax?: string;
}

export interface IRxChangeResponseMedication {
  drugDescription: string;
  ndc: string;
  drugDbCode?: string;
  drugDbQualifier?: string;
  strengthValue?: string;
  quantity: number;
  quantityUnitOfMeasure: string;
  quantityCodeListQualifier?: string;
  daysSupply: number;
  numberOfRefills: number;
  sigText: string;
  substitutions?: number;
  writtenDate?: string;
}

export interface IRxChangeResponseRequest {
  relatesToMessageId: string;
  messageRequestCode: string;
  responseType: RxChangeResponseType;
  note?: string;
  denialReasonCode?: string;
  denialReason?: string;
  personId?: string;
  profileId?: string;
  prescriber: IRxChangeResponsePrescriber;
  medication?: IRxChangeResponseMedication;
}

export interface IRxChangeResponseResult {
  success?: boolean;
  statusCode?: string;
  statusDescription?: string;
  messageId?: string;
  sentMessageId?: string;
}

/** Returns the first problem with the form, or null when it can be submitted. */
export const validateRxChangeResponseForm = (form: IRxChangeResponseForm): string | null => {
  const relatesTo = form.relatesToMessageId.trim();
  if (!relatesTo) return "RelatesToMessageID is required";
  if (relatesTo.length > MAX_RELATES_TO_MESSAGE_ID_LENGTH) {
    return `RelatesToMessageID must be at most ${MAX_RELATES_TO_MESSAGE_ID_LENGTH} characters`;
  }
  if (!determineIdentitySource(form.patientId)) {
    return "Enter a valid Person ID (amzn1.actor.person.oid...) or Profile ID (amzn1.pharmacy.profile.id...)";
  }
  if (form.responseType === "DENIED") {
    return form.denialReasonCode ? null : "Select a denial reason";
  }
  if (form.note.trim().length > MAX_NOTE_LENGTH) {
    return `Note must be at most ${MAX_NOTE_LENGTH} characters`;
  }
  const { medication } = form;
  if (!medication.ndc.trim() || !medication.sigText.trim()) return "Medication NDC and instructions are required";
  if (!isWholeNumber(medication.quantity.value, 1) || !isWholeNumber(medication.daysSupply, 1)) {
    return "Medication quantity and days supply must be whole numbers greater than 0";
  }
  if (!isWholeNumber(medication.numberOfRefills, 0)) return "Medication refills must be a whole number, 0 or more";
  return null;
};

const isWholeNumber = (value: string, min: number): boolean =>
  /^\d+$/.test(value.trim()) && Number(value) >= min;

const optional = (value: string): string | undefined => (value.trim() ? value.trim() : undefined);

const toPrescriber = (prescriber: IPrescriberDetails): IRxChangeResponsePrescriber => ({
  npi: prescriber.npi,
  deaNumber: prescriber.deaNumber,
  firstName: prescriber.firstName,
  lastName: prescriber.lastName,
  practiceName: prescriber.clinicName,
  addressLine1: prescriber.addressLine1,
  city: prescriber.city,
  state: prescriber.state,
  postalCode: prescriber.zipCode,
  phone: prescriber.phoneNumber,
  fax: prescriber.fax?.[0]?.Number,
});

const toMedication = (medication: IMedicationDetails): IRxChangeResponseMedication => ({
  drugDescription: medication.drugDescription,
  ndc: medication.ndc.trim(),
  drugDbCode: medication.drugDB.code !== PLACEHOLDER_DRUG_DB_CODE ? medication.drugDB.code : undefined,
  drugDbQualifier: medication.drugDB.code !== PLACEHOLDER_DRUG_DB_CODE ? medication.drugDB.qualifier : undefined,
  strengthValue: medication.strength.value,
  quantity: Number(medication.quantity.value),
  quantityUnitOfMeasure: medication.quantity.unitOfMeasureCode,
  quantityCodeListQualifier: medication.quantity.qualifierCode,
  daysSupply: Number(medication.daysSupply),
  numberOfRefills: Number(medication.numberOfRefills),
  sigText: medication.sigText.trim(),
  substitutions: medication.dispenseAsWritten,
  writtenDate: medication.writtenDate ? `${medication.writtenDate}T00:00:00Z` : undefined,
});

/** Body for POST /prescription-messaging/ingest-rx-change-response. */
export const buildRxChangeResponseRequest = (form: IRxChangeResponseForm): IRxChangeResponseRequest => {
  const patientId = form.patientId.trim();
  const base: IRxChangeResponseRequest = {
    relatesToMessageId: form.relatesToMessageId.trim(),
    messageRequestCode: form.messageRequestCode,
    responseType: form.responseType,
    ...(determineIdentitySource(patientId) === "profileId" ? { profileId: patientId } : { personId: patientId }),
    prescriber: toPrescriber(form.prescriber),
  };
  if (form.responseType === "DENIED") {
    return { ...base, denialReasonCode: form.denialReasonCode, denialReason: optional(form.denialReason) };
  }
  return { ...base, note: optional(form.note), medication: toMedication(form.medication) };
};
